/**
 * Single-sheet Excel: vendor blocks as tables (header + rows + subtotal), then grand total.
 * Uses exceljs (already in package.json).
 */
import ExcelJS from "exceljs";

function colToLetter(index1Based) {
  let n = index1Based;
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s || "A";
}

const THIN_BORDER = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

/**
 * @param {object} options
 * @param {string} options.filename - without extension
 * @param {string} options.reportTitle
 * @param {string} options.dateRangeLabel
 * @param {string} options.vendorFilterLabel
 * @param {Array<{ vendor_name: string, rows: Array, totalAmount: number }>} options.vendorSections
 * @param {string[]} options.headers
 * @param {(row: object) => any[]} options.mapRowToCells
 */
export async function exportUpcomingVendorExcel({
  filename,
  reportTitle,
  dateRangeLabel,
  vendorFilterLabel,
  vendorSections,
  headers,
  mapRowToCells,
}) {
  const numCols = headers.length;
  const lastCol = colToLetter(numCols);

  const wb = new ExcelJS.Workbook();
  wb.creator = "DataVision Accounting";
  const ws = wb.addWorksheet("Schedule", {
    properties: { tabColor: { argb: "FF1890FF" } },
  });

  ws.columns = headers.map((_, i) => ({
    width: i === 0 ? 22 : i === numCols - 1 ? 16 : 14,
  }));

  let r = 1;
  ws.mergeCells(`A${r}:${lastCol}${r}`);
  const titleCell = ws.getCell(`A${r}`);
  titleCell.value = reportTitle;
  titleCell.font = { bold: true, size: 14 };
  titleCell.alignment = { vertical: "middle" };
  r += 1;

  ws.mergeCells(`A${r}:${lastCol}${r}`);
  ws.getCell(`A${r}`).value = `Date range: ${dateRangeLabel}`;
  r += 1;

  ws.mergeCells(`A${r}:${lastCol}${r}`);
  ws.getCell(`A${r}`).value = `Vendors: ${vendorFilterLabel}`;
  r += 1;

  ws.addRow([]);
  r += 1;

  let grandTotal = 0;
  let lineCount = 0;

  vendorSections.forEach((section) => {
    const vendorName = section.vendor_name || "Unknown";
    const rows = section.rows || [];
    const subtotal = Number(section.totalAmount) || 0;
    grandTotal += subtotal;
    lineCount += rows.length;

    ws.mergeCells(`A${r}:${lastCol}${r}`);
    const sec = ws.getCell(`A${r}`);
    sec.value = `Vendor: ${vendorName}`;
    sec.font = { bold: true, size: 12 };
    sec.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFF0F0F0" },
    };
    sec.border = THIN_BORDER;
    r += 1;

    const headerRow = ws.addRow(headers);
    headerRow.font = { bold: true };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE6F7FF" },
    };
    headerRow.eachCell((cell) => {
      cell.border = THIN_BORDER;
    });
    r += 1;

    rows.forEach((row) => {
      const dataRow = ws.addRow(mapRowToCells(row));
      dataRow.eachCell((cell) => {
        cell.border = THIN_BORDER;
      });
      r += 1;
    });

    const subCells = new Array(numCols).fill("");
    subCells[0] = `Subtotal — ${vendorName}`;
    subCells[numCols - 1] = subtotal;
    const subRow = ws.addRow(subCells);
    subRow.font = { bold: true };
    subRow.getCell(numCols).numFmt = "#,##0.00";
    subRow.eachCell((cell) => {
      cell.border = THIN_BORDER;
    });
    r += 1;

    ws.addRow([]);
    r += 1;
  });

  const grandCells = new Array(numCols).fill("");
  grandCells[0] = "Grand total (all vendors)";
  if (numCols > 2) {
    grandCells[1] = `${lineCount} line item(s)`;
  }
  grandCells[numCols - 1] = grandTotal;
  const grandRow = ws.addRow(grandCells);
  grandRow.font = { bold: true, size: 12 };
  grandRow.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFF7E6" },
  };
  grandRow.getCell(numCols).numFmt = "#,##0.00";
  grandRow.eachCell((cell) => {
    cell.border = THIN_BORDER;
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${filename}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
